import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

import Tooltip from '@mui/material/Tooltip';
import { GridActionsCellItem } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { ActorTypeEnum, ThirdPartyTypeEnum } from '@e-socna/data-layer';
import { BooleanFilter, buildPath } from '@e-socna/utils';
import { EMPTY_STRING } from '@e-socna/empty';

import { PATH_MISSION_DEFAULT } from '../../app/constants/paths.constant';
import { formatOrganizationIdentifierValue } from '../../utils/format-organization-identifier.util';
import { MISSION_SPACE_PARAM } from '../../app/utils/mission-space.util';
import { MissionSpaceEnum } from '../../app/enums/mission-space.enum';
import type {
  ReferencesContext_ActorCategory,
  ReferencesContext_Civility,
  ReferencesContext_RepresentativeType,
} from '../../contexts/ReferencesContext';

import type { ParticipationsCmp_OneThirdParty_Participation } from '.';

interface ActionProps {
  readonly participation: ParticipationsCmp_OneThirdParty_Participation;
}

const Action = ({ participation }: Readonly<ActionProps>) => {
  const navigate = useNavigate();

  const consult = useCallback(() => {
    // A collaboration opened under "missions" is redirected to its space.
    const path = buildPath(PATH_MISSION_DEFAULT, {
      folderId: participation.mission.folderId,
      missionId: participation.mission._id,
      [MISSION_SPACE_PARAM]: MissionSpaceEnum.Missions,
    });
    void navigate(path);
  }, [navigate, participation.mission.folderId, participation.mission._id]);

  const actionLabel = 'Consulter la fiche';

  return (
    <GridActionsCellItem
      icon={
        <Tooltip title={actionLabel}>
          <VisibilityIcon />
        </Tooltip>
      }
      label={actionLabel}
      onClick={consult}
    />
  );
};

interface Params {
  readonly civilities: readonly ReferencesContext_Civility[];
  readonly actorCategories: readonly ReferencesContext_ActorCategory[];
  readonly representativeTypes: readonly ReferencesContext_RepresentativeType[];
  readonly kind?: ThirdPartyTypeEnum;
}

const getActor = (
  row: Readonly<ParticipationsCmp_OneThirdParty_Participation>,
) => {
  const actor = [
    ...row.mission.consolidation.internalActors,
    ...row.mission.consolidation.externalActors,
  ].find((externalActor) => externalActor._id === row.actorId);

  return actor;
};

export const getParticipationColumns = ({
  civilities,
  actorCategories,
  representativeTypes,
  kind,
}: Readonly<Params>): readonly GridColDef<ParticipationsCmp_OneThirdParty_Participation>[] => {
  const columns = [
    {
      field: 'actions',
      headerName: 'Actions',
      description: 'Actions',
      editable: false,
      sortable: false,
      type: 'actions' as const,
      getActions: (
        params: GridRowParams<ParticipationsCmp_OneThirdParty_Participation>,
      ) => [<Action key="consult" participation={params.row} />],
    },
    {
      field: 'missionCode',
      headerName: 'Code Mission',
      width: 350,
      description: 'Code Mission',
      editable: false,
      sortable: false,
      type: 'string' as const,
      valueGetter: (
        _: unknown,
        row: ParticipationsCmp_OneThirdParty_Participation,
      ) => row.mission.code,
    },
    {
      field: 'actor',
      headerName: 'Acteur',
      width: 350,
      description: 'Acteur',
      editable: false,
      sortable: false,
      type: 'string' as const,
      valueGetter: (
        _: unknown,
        row: ParticipationsCmp_OneThirdParty_Participation,
      ) => {
        const actor = getActor(row);

        const actorThirdParty = actor?.thirdParty;

        if (!actorThirdParty) {
          return 'Inconnu';
        }

        switch (actorThirdParty.__typename) {
          case 'Person':
          // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
          case ThirdPartyTypeEnum.Person: {
            if (
              !('civilityId' in actorThirdParty) ||
              !('fullName' in actorThirdParty)
            ) {
              return 'Inconnu';
            }

            const civility = civilities.find(
              (civility) => civility._id === actorThirdParty.civilityId,
            );

            const civilityPrefix = civility
              ? `${civility.label} `
              : EMPTY_STRING;

            return `${civilityPrefix}${actorThirdParty.fullName}`;
          }
          case 'Organization':
          // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
          case ThirdPartyTypeEnum.Organization: {
            const organizationIdentifierValue =
              formatOrganizationIdentifierValue(actorThirdParty.identifier);
            const organizationIdentifierPrefix = organizationIdentifierValue
              ? `${organizationIdentifierValue} - `
              : EMPTY_STRING;
            return `${organizationIdentifierPrefix}${actorThirdParty.name}${
              actorThirdParty.tradingName
                ? ` - ${actorThirdParty.tradingName}`
                : EMPTY_STRING
            }`;
          }
          default:
            throw new Error('Unexpected actorThirdParty');
        }
      },
    },
    {
      field: 'actorCategory',
      headerName: "Catégories d'Acteur",
      width: 350,
      description: "Catégories d'Acteur",
      editable: false,
      sortable: false,
      type: 'string' as const,
      valueGetter: (
        _: unknown,
        row: ParticipationsCmp_OneThirdParty_Participation,
      ) => {
        const actor = getActor(row);

        const actorCategoryIds = actor?.categoryIds;

        const categories = actorCategoryIds
          ?.map((actorCategoryId) =>
            actorCategories.find(
              (category) => category._id === actorCategoryId,
            ),
          )
          .filter(BooleanFilter);

        return (
          categories
            ?.map((category) => category.label)
            .filter(BooleanFilter)
            .join(', ') ?? undefined
        );
      },
    },
  ];

  if (kind === ThirdPartyTypeEnum.Person) {
    const personColumns = [
      {
        field: 'representative',
        headerName: 'Représentant',
        width: 350,
        description: 'Représentant',
        editable: false,
        sortable: false,
        type: 'string' as const,
        valueGetter: (
          _: unknown,
          row: ParticipationsCmp_OneThirdParty_Participation,
        ) => {
          const actor = getActor(row);

          if (actor?.__typename !== ActorTypeEnum.ActorOrganization) {
            return undefined;
          }

          const representative = actor.representatives.find(
            (representative) => representative._id === row.representativeId,
          );

          const representativePerson = representative?.person;

          if (!representativePerson) {
            return undefined;
          }

          const civility = civilities.find(
            (civility) => civility._id === representativePerson.civilityId,
          );

          const civilityPrefix = civility ? `${civility.label} ` : EMPTY_STRING;

          return `${civilityPrefix}${representativePerson.fullName}`;
        },
      },
      {
        field: 'representativeTypes',
        headerName: 'Types de Représentant',
        width: 350,
        description: 'Types de Représentant',
        editable: false,
        sortable: false,
        type: 'string' as const,
        valueGetter: (
          _: unknown,
          row: ParticipationsCmp_OneThirdParty_Participation,
        ) => {
          const actor = getActor(row);

          if (actor?.__typename !== ActorTypeEnum.ActorOrganization) {
            return undefined;
          }

          const representative = actor.representatives.find(
            (representative) => representative._id === row.representativeId,
          );

          const representativeTypeIds = representative?.typeIds;

          const types = representativeTypeIds
            ?.map((id) => representativeTypes.find((type) => type._id === id))
            .filter(BooleanFilter);

          return (
            types
              ?.map((type) => type.label)
              .filter(BooleanFilter)
              .join(', ') ?? undefined
          );
        },
      },
    ] as const;

    columns.push(...personColumns);
  }

  return columns;
};
